type UnitDef = {
    name: string;
    toMeters: number;
    aliases?: string[];
};

type UnitMap = Record<string, UnitDef>;

/**
 * Dimension converter with N-level compound support, aliases, and decimals.
 */
export class UnitConversionParser {
    private static unitTable: UnitMap = {};

    /** Initialize built-in units */
    static init() {
        const units: UnitDef[] = [
            // Metric
            { name: 'nm', toMeters: 1e-9, aliases: ['nanometer', 'nanometers'] },
            { name: 'µm', toMeters: 1e-6, aliases: ['micrometer', 'micrometers', 'micron', 'microns'] },
            { name: 'mm', toMeters: 0.001, aliases: ['millimeter', 'millimeters'] },
            { name: 'cm', toMeters: 0.01, aliases: ['centimeter', 'centimeters'] },
            { name: 'm', toMeters: 1, aliases: ['meter', 'meters', 'metre', 'metres'] },
            { name: 'km', toMeters: 1000, aliases: ['kilometer', 'kilometers', 'kilometre', 'kilometres'] },

            // Imperial / US customary
            { name: 'in', toMeters: 0.0254, aliases: ['inch', 'inches', '"', '″'] },
            { name: 'ft', toMeters: 0.3048, aliases: ['foot', 'feet', "'", '′'] },
            { name: 'yd', toMeters: 0.9144, aliases: ['yard', 'yards'] },
            { name: 'mi', toMeters: 1609.34, aliases: ['mile', 'miles'] },

            // Engineering / survey
            { name: 'mil', toMeters: 0.0254 / 1000, aliases: ['thou'] },
            { name: 'nmi', toMeters: 1852, aliases: ['nauticalmile', 'nauticalmiles'] },
            { name: 'chain', toMeters: 20.1168, aliases: [] },
            { name: 'rod', toMeters: 5.0292, aliases: [] },
        ];

        for (const u of units) UnitConversionParser.registerUnit(u);
    }

    /** Register a unit and all its aliases */
    static registerUnit(unit: UnitDef) {
        const aliases = new Set<string>([unit.name.toLowerCase()]);
        if (unit.aliases) for (const a of unit.aliases) aliases.add(a.toLowerCase());
        for (const a of aliases) UnitConversionParser.unitTable[a] = unit;
    }

    /**
     * Convert a dimension string to a format string with placeholders.
     * Supports N-level compounds like "{x yd} {y ft} {z in}"
     */
   public convert(inputStr: string, format: string): string {
    try {
        format = format.replace(/[“”]/g, '"').replace(/[‘’]/g, "'");

        const inputMatch = inputStr.match(/([\d.]+)\s*([a-zA-Z"'′″µ]+)/);
        if (!inputMatch) return this.warn("Invalid input format (expected '<number><unit>')", format);

        const [, valueStr, unitRaw] = inputMatch;
        const value = parseFloat(valueStr);
        const inputUnit = unitRaw.toLowerCase().trim();
        const src = UnitConversionParser.unitTable[inputUnit];
        if (!src) return this.warn(`Unsupported input unit: '${unitRaw}'`, format);

        let remainingMeters = value * src.toMeters;

        // --- Updated regex for decimals ---
        const placeholders = [...format.matchAll(/\{\{\s*([a-zA-Z])(?:\.([a-zA-Z]+))?\s*([a-zA-Z"'′″µ]+)\s*\}\}/g)];
        if (placeholders.length === 0) return this.warn('Invalid format pattern (no placeholders found)', format);

        const results: { fullMatch: string; formatted: string; unitRaw: string }[] = [];

        for (const ph of placeholders) {
            const [fullMatch, , decimalsRaw, unitRaw] = ph;
            const decimals = decimalsRaw ? decimalsRaw.length : 0;
            const unitKey = unitRaw.toLowerCase().trim();
            const def = UnitConversionParser.unitTable[unitKey];
            if (!def) return this.warn(`Unsupported target unit: '${unitRaw}'`, format);

            const amount = remainingMeters / def.toMeters;
            let displayAmount: number;

            if (decimals > 0) {
                displayAmount = parseFloat(amount.toFixed(decimals));
                remainingMeters -= displayAmount * def.toMeters;
            } else {
                const whole = Math.floor(amount);
                displayAmount = whole;
                remainingMeters -= whole * def.toMeters;
            }

            const formatted = displayAmount.toFixed(decimals);
            results.push({ fullMatch, formatted, unitRaw });
        }

        let output = format;
        for (const r of results) {
            output = output.replace(r.fullMatch, `${r.formatted} ${r.unitRaw}`);
        }

        return output;
    } catch (err: any) {
        return this.warn(`Unexpected error: ${err.message || err}`, format);
    }
}


    /** Helper: warning instead of throwing */
    private warn(message: string, fallback: string): string {
        console.warn(`[DimensionConverter Warning] ${message}`);
        return fallback;
    }
}

// Initialize units
UnitConversionParser.init();